import { environment } from '../../../environments/environment';

export interface ChatCitation {
  index: number;
  contentId: string;
  contentTitle: string;
  chunkType: string;
  page?: number;
  slide?: number;
  startTime?: number;
  endTime?: number;
  sectionTitle?: string;
}

export interface ChatFrame {
  delta: string;
  done: boolean;
  citations: ChatCitation[];
  error: string;
}

export interface ChatHistoryMessage {
  role: 'user' | 'assistant';
  text: string;
}

export interface ChatRequestBody {
  organization_id: string;
  team_id: string;
  scope: { kind: string; view_id?: string; content_id?: string; block_id?: string };
  history: ChatHistoryMessage[];
}

/** Parse a partial SSE buffer into complete frames + leftover tail. */
export function parseSseBuffer(buffer: string): { frames: ChatFrame[]; rest: string } {
  const parts = buffer.split('\n\n');
  const rest = parts.pop() ?? '';
  const frames: ChatFrame[] = [];
  for (const part of parts) {
    const line = part.trim();
    if (!line.startsWith('data:')) continue;
    try {
      const obj = JSON.parse(line.slice(line.indexOf('data:') + 5).trim());
      frames.push({
        delta: obj.delta ?? '',
        done: !!obj.done,
        citations: Array.isArray(obj.citations) ? obj.citations : [],
        error: obj.error ?? '',
      });
    } catch {
      // ignore malformed frame
    }
  }
  return { frames, rest };
}

/**
 * POST to the chat endpoint and yield frames as they stream in.
 * Throws on non-OK HTTP. Pass an AbortSignal to cancel.
 */
export async function* streamChat(body: ChatRequestBody, signal?: AbortSignal): AsyncGenerator<ChatFrame> {
  const token = localStorage.getItem('accessToken');
  const res = await fetch(`${environment.apiUrl}/content/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok || !res.body) {
    let message = `Chat request failed (${res.status})`;
    try {
      const err = await res.json();
      message = err?.error?.message || message;
    } catch { /* non-JSON */ }
    throw new Error(message);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const { frames, rest } = parseSseBuffer(buffer);
    buffer = rest;
    for (const frame of frames) yield frame;
  }
  const { frames } = parseSseBuffer(buffer + '\n\n');
  for (const frame of frames) yield frame;
}
